(() => {
  'use strict';

  const MAX_PRODUCTS = 3;
  const STORAGE_KEY = 'lpr_compare_selection_v1';

  let selectedProducts = [];
  let lastFocusedElement = null;
  let mutationTimer = null;

  function getCollectionHandle() {
    const match = window.location.pathname.match(/\/collections\/([^/?#]+)/);

    if (!match) {
      return window.location.pathname;
    }

    try {
      return decodeURIComponent(match[1]);
    } catch (error) {
      return match[1];
    }
  }

  const currentCollection = getCollectionHandle();

  function escapeHTML(value) {
    return String(value ?? '')
      .replaceAll('&', '&amp;')
      .replaceAll('<', '&lt;')
      .replaceAll('>', '&gt;')
      .replaceAll('"', '&quot;')
      .replaceAll("'", '&#039;');
  }

  function normalizeProduct(product) {
    return {
      id: String(product?.id ?? ''),
      variantId: String(product?.variantId ?? ''),
      handle: String(product?.handle ?? ''),
      url: String(product?.url ?? '#'),
      title: String(product?.title ?? ''),
      vendor: String(product?.vendor ?? ''),
      price: String(product?.price ?? ''),
      image: product?.image ? String(product.image) : '',
      available: Boolean(product?.available),
      specs:
        product?.specs && typeof product.specs === 'object'
          ? product.specs
          : {},
    };
  }

  function loadSelection() {
    try {
      const storedValue = window.sessionStorage.getItem(STORAGE_KEY);

      if (!storedValue) {
        selectedProducts = [];
        return;
      }

      const storedData = JSON.parse(storedValue);

      if (
        storedData?.collection !== currentCollection ||
        !Array.isArray(storedData?.products)
      ) {
        selectedProducts = [];
        window.sessionStorage.removeItem(STORAGE_KEY);
        return;
      }

      selectedProducts = storedData.products
        .map(normalizeProduct)
        .filter((product) => product.id)
        .slice(0, MAX_PRODUCTS);
    } catch (error) {
      selectedProducts = [];
    }
  }

  function saveSelection() {
    try {
      window.sessionStorage.setItem(
        STORAGE_KEY,
        JSON.stringify({
          collection: currentCollection,
          products: selectedProducts,
        })
      );
    } catch (error) {
      /* La comparaison fonctionne même sans sessionStorage. */
    }
  }

  function getProductData(button) {
    const card = button.closest('.card-wrapper');

    if (!card) {
      return null;
    }

    const dataElement = card.querySelector(
      '.lpr-compare-product-data'
    );

    if (!dataElement) {
      return null;
    }

    try {
      return normalizeProduct(
        JSON.parse(dataElement.textContent)
      );
    } catch (error) {
      console.error(
        'Le Prix Royal : données de comparaison invalides.',
        error
      );

      return null;
    }
  }

  function isSelected(productId) {
    return selectedProducts.some(
      (product) => product.id === String(productId)
    );
  }

  function addProduct(product) {
    if (!product?.id || isSelected(product.id)) {
      return;
    }

    if (selectedProducts.length >= MAX_PRODUCTS) {
      showTemporaryMessage(
        'Vous pouvez comparer un maximum de 3 produits.'
      );

      return;
    }

    selectedProducts.push(product);
    saveSelection();
    renderInterface();
  }

  function removeProduct(productId) {
    selectedProducts = selectedProducts.filter(
      (product) => product.id !== String(productId)
    );

    saveSelection();
    renderInterface();

    if (selectedProducts.length < 2) {
      closeModal();
    }
  }

  function toggleProduct(button) {
    const productId = String(
      button.dataset.productId ?? ''
    );

    if (!productId) {
      return;
    }

    if (isSelected(productId)) {
      removeProduct(productId);
      return;
    }

    const product = getProductData(button);

    if (product) {
      addProduct(product);
    }
  }

  function createInterface() {
    if (document.querySelector('[data-lpr-compare-interface]')) {
      return;
    }

    const interfaceElement = document.createElement('div');

    interfaceElement.setAttribute(
      'data-lpr-compare-interface',
      ''
    );

    interfaceElement.innerHTML = `
      <div
        class="lpr-compare-bar"
        data-lpr-compare-bar
        hidden
      >
        <div class="lpr-compare-bar__inner">
          <div class="lpr-compare-bar__summary">
            <svg
              class="lpr-compare-bar__icon"
              viewBox="0 0 24 24"
              aria-hidden="true"
              focusable="false"
            >
              <path
                d="M4 8h14"
                fill="none"
                stroke="currentColor"
                stroke-width="1.8"
                stroke-linecap="round"
              ></path>

              <path
                d="M15 5l3 3-3 3"
                fill="none"
                stroke="currentColor"
                stroke-width="1.8"
                stroke-linecap="round"
                stroke-linejoin="round"
              ></path>

              <path
                d="M20 16H6"
                fill="none"
                stroke="currentColor"
                stroke-width="1.8"
                stroke-linecap="round"
              ></path>

              <path
                d="M9 13l-3 3 3 3"
                fill="none"
                stroke="currentColor"
                stroke-width="1.8"
                stroke-linecap="round"
                stroke-linejoin="round"
              ></path>
            </svg>

            <div>
              <p
                class="lpr-compare-bar__count"
                data-lpr-compare-count
              ></p>

              <p
                class="lpr-compare-bar__message"
                data-lpr-compare-message
              ></p>
            </div>
          </div>

          <div
            class="lpr-compare-bar__products"
            data-lpr-compare-products
          ></div>

          <button
            type="button"
            class="button lpr-compare-bar__button"
            data-lpr-open-comparison
            disabled
          >
            Comparer
          </button>
        </div>

        <p
          class="lpr-compare-bar__temporary-message"
          data-lpr-temporary-message
          role="status"
          aria-live="polite"
          hidden
        ></p>
      </div>

      <div
        class="lpr-compare-modal"
        data-lpr-compare-modal
        hidden
      >
        <div
          class="lpr-compare-modal__backdrop"
          data-lpr-close-comparison
        ></div>

        <div
          class="lpr-compare-modal__dialog"
          role="dialog"
          aria-modal="true"
          aria-labelledby="LprCompareModalTitle"
          tabindex="-1"
        >
          <div class="lpr-compare-modal__header">
            <div>
              <p class="lpr-compare-modal__eyebrow">
                Le Prix Royal
              </p>

              <h2
                class="lpr-compare-modal__title"
                id="LprCompareModalTitle"
              >
                Comparer les produits
              </h2>
            </div>

            <button
              type="button"
              class="lpr-compare-modal__close"
              aria-label="Fermer la comparaison"
              data-lpr-close-comparison
            >
              <svg
                viewBox="0 0 20 20"
                aria-hidden="true"
                focusable="false"
              >
                <path
                  d="M4.5 4.5l11 11m0-11-11 11"
                  fill="none"
                  stroke="currentColor"
                  stroke-width="1.7"
                  stroke-linecap="round"
                ></path>
              </svg>
            </button>
          </div>

          <div
            class="lpr-compare-modal__content"
            data-lpr-comparison-content
          ></div>
        </div>
      </div>
    `;

    document.body.appendChild(interfaceElement);
  }

  function getStatusMessage() {
    const count = selectedProducts.length;

    if (count === 1) {
      return {
        count: '1 produit sélectionné',
        message:
          'Choisissez 1 ou 2 autres produits à comparer.',
      };
    }

    if (count === 2) {
      return {
        count: '2 produits sélectionnés',
        message:
          'Vous pouvez ajouter 1 autre produit.',
      };
    }

    return {
      count: '3 produits sélectionnés',
      message:
        'Votre sélection est prête à comparer.',
    };
  }

  function renderProductChip(product) {
    const image = product.image
      ? `
        <img
          class="lpr-compare-chip__image"
          src="${escapeHTML(product.image)}"
          alt=""
          width="58"
          height="58"
          loading="lazy"
        >
      `
      : `
        <div
          class="lpr-compare-chip__image lpr-compare-chip__image--empty"
          aria-hidden="true"
        ></div>
      `;

    return `
      <div
        class="lpr-compare-chip"
        data-lpr-selected-product="${escapeHTML(product.id)}"
      >
        ${image}

        <div class="lpr-compare-chip__details">
          <p class="lpr-compare-chip__title">
            ${escapeHTML(product.title)}
          </p>

          <p class="lpr-compare-chip__price">
            ${escapeHTML(product.price)}
          </p>
        </div>

        <button
          type="button"
          class="lpr-compare-chip__remove"
          data-lpr-remove-product="${escapeHTML(product.id)}"
          aria-label="Retirer ${escapeHTML(product.title)} de la comparaison"
        >
          <svg
            viewBox="0 0 20 20"
            aria-hidden="true"
            focusable="false"
          >
            <path
              d="M5 5l10 10m0-10L5 15"
              fill="none"
              stroke="currentColor"
              stroke-width="1.6"
              stroke-linecap="round"
            ></path>
          </svg>
        </button>
      </div>
    `;
  }

  function updateButtons() {
    const buttons = document.querySelectorAll(
      '[data-lpr-compare-button]'
    );

    const limitReached =
      selectedProducts.length >= MAX_PRODUCTS;

    buttons.forEach((button) => {
      const productId = String(
        button.dataset.productId ?? ''
      );

      const selected = isSelected(productId);

      button.classList.toggle('is-selected', selected);
      button.classList.toggle(
        'is-limit-reached',
        limitReached && !selected
      );

      button.setAttribute(
        'aria-pressed',
        selected ? 'true' : 'false'
      );

      button.disabled = limitReached && !selected;

      button.title = selected
        ? 'Retirer de la comparaison'
        : 'Ajouter à la comparaison';

      const productTitle =
        button
          .closest('.card-wrapper')
          ?.querySelector('.card__heading a')
          ?.textContent?.trim() ?? 'ce produit';

      button.setAttribute(
        'aria-label',
        selected
          ? `Retirer ${productTitle} de la comparaison`
          : `Ajouter ${productTitle} à la comparaison`
      );
    });
  }

  function renderInterface() {
    createInterface();
    updateButtons();

    const bar = document.querySelector(
      '[data-lpr-compare-bar]'
    );

    const countElement = document.querySelector(
      '[data-lpr-compare-count]'
    );

    const messageElement = document.querySelector(
      '[data-lpr-compare-message]'
    );

    const productsElement = document.querySelector(
      '[data-lpr-compare-products]'
    );

    const compareButton = document.querySelector(
      '[data-lpr-open-comparison]'
    );

    if (
      !bar ||
      !countElement ||
      !messageElement ||
      !productsElement ||
      !compareButton
    ) {
      return;
    }

    if (selectedProducts.length === 0) {
      bar.hidden = true;
      return;
    }

    const status = getStatusMessage();

    bar.hidden = false;
    countElement.textContent = status.count;
    messageElement.textContent = status.message;

    productsElement.innerHTML = selectedProducts
      .map(renderProductChip)
      .join('');

    compareButton.textContent =
      `Comparer (${selectedProducts.length})`;

    compareButton.disabled =
      selectedProducts.length < 2;
  }

  function formatValue(value) {
    if (
      value === null ||
      value === undefined ||
      value === ''
    ) {
      return '—';
    }

    if (typeof value === 'boolean') {
      return value ? 'Oui' : 'Non';
    }

    if (Array.isArray(value)) {
      const formattedValues = value
        .map(formatValue)
        .filter((item) => item !== '—');

      return formattedValues.length
        ? formattedValues.join(', ')
        : '—';
    }

    if (typeof value === 'object') {
      if (
        Object.prototype.hasOwnProperty.call(
          value,
          'value'
        )
      ) {
        const unit = value.unit
          ? ` ${String(value.unit)
              .toLowerCase()
              .replaceAll('_', ' ')}`
          : '';

        return `${formatValue(value.value)}${unit}`;
      }

      const objectValues = Object.values(value)
        .map(formatValue)
        .filter((item) => item !== '—');

      return objectValues.length
        ? objectValues.join(', ')
        : '—';
    }

    return String(value);
  }

  function normalizedDifferenceValue(value) {
    return formatValue(value)
      .trim()
      .toLowerCase()
      .replace(/\s+/g, ' ');
  }

  function rowIsDifferent(values) {
    const usefulValues = values
      .map(normalizedDifferenceValue)
      .filter((value) => value && value !== '—');

    return new Set(usefulValues).size > 1;
  }

  function getComparisonRows() {
    return [
      {
        label: 'Prix',
        getValue: (product) => product.price,
      },
      {
        label: 'Marque',
        getValue: (product) => product.vendor,
      },
      {
        label: 'Type d’écran',
        getValue: (product) =>
          product.specs.screenType,
      },
      {
        label: 'Taille de l’écran',
        getValue: (product) =>
          product.specs.displaySize,
      },
      {
        label: 'Résolution',
        getValue: (product) =>
          product.specs.resolution,
      },
      {
        label: 'Fréquence de rafraîchissement',
        getValue: (product) =>
          product.specs.refreshRate,
      },
      {
        label: 'Technologie d’affichage',
        getValue: (product) =>
          product.specs.displayTechnology,
      },
      {
        label: 'Temps de réponse',
        getValue: (product) =>
          product.specs.responseTime,
      },
      {
        label: 'Connectivité',
        getValue: (product) =>
          product.specs.connectivity,
      },
      {
        label: 'Écran incurvé',
        getValue: (product) =>
          product.specs.curvedScreen,
      },
      {
        label: 'Écran tactile',
        getValue: (product) =>
          product.specs.touchScreen,
      },
      {
        label: 'Gaming',
        getValue: (product) =>
          product.specs.gaming,
      },
    ];
  }

  function renderComparisonProduct(product) {
  const image = product.image
    ? `
      <img
        class="lpr-comparison-product__image"
        src="${escapeHTML(product.image)}"
        alt="${escapeHTML(product.title)}"
        width="220"
        height="220"
      >
    `
    : '';

  const canAddToCart = Boolean(
    product.available && product.variantId
  );

  const addButtonText = canAddToCart
    ? 'Ajouter au panier'
    : 'Épuisé';

  return `
    <div class="lpr-comparison-product">
      <a
        class="lpr-comparison-product__image-link"
        href="${escapeHTML(product.url)}"
      >
        ${image}
      </a>

      <p class="lpr-comparison-product__vendor">
        ${escapeHTML(product.vendor)}
      </p>

      <a
        class="lpr-comparison-product__title"
        href="${escapeHTML(product.url)}"
      >
        ${escapeHTML(product.title)}
      </a>

      <p class="lpr-comparison-product__price">
        ${escapeHTML(product.price)}
      </p>

      <div class="lpr-comparison-product__actions">
        <product-form
          class="lpr-comparison-product__form"
          data-section-id="lpr-comparison"
        >
          <form
            method="post"
            action="/cart/add"
            id="LprCompareAdd-${escapeHTML(product.id)}"
            accept-charset="UTF-8"
            class="form"
            enctype="multipart/form-data"
            novalidate="novalidate"
            data-type="add-to-cart-form"
          >
            <input
              type="hidden"
              name="id"
              value="${escapeHTML(product.variantId)}"
              class="product-variant-id"
              ${canAddToCart ? '' : 'disabled'}
            >

            <button
              type="submit"
              name="add"
              class="button lpr-comparison-product__add"
              ${canAddToCart ? '' : 'disabled'}
            >
              <span>
                ${addButtonText}
              </span>

              <span class="loading__spinner hidden">
                <svg
                  class="spinner"
                  viewBox="0 0 66 66"
                  xmlns="http://www.w3.org/2000/svg"
                  aria-hidden="true"
                  focusable="false"
                >
                  <circle
                    class="path"
                    fill="none"
                    stroke-width="6"
                    cx="33"
                    cy="33"
                    r="30"
                  ></circle>
                </svg>
              </span>
            </button>
          </form>
        </product-form>

        <a
          class="button button--secondary lpr-comparison-product__link"
          href="${escapeHTML(product.url)}"
        >
          Voir le produit
        </a>
      </div>
    </div>
  `;
}

  function buildComparisonTable() {
    const content = document.querySelector(
      '[data-lpr-comparison-content]'
    );

    if (!content) {
      return;
    }

    const rows = getComparisonRows();

    const productHeaders = selectedProducts
      .map(
        (product) => `
          <th
            class="lpr-comparison-table__product-heading"
            scope="col"
          >
            ${renderComparisonProduct(product)}
          </th>
        `
      )
      .join('');

    const tableRows = rows
      .map((row) => {
        const values = selectedProducts.map(
          row.getValue
        );

        const different = rowIsDifferent(values);

        const cells = values
          .map(
            (value) => `
              <td>
                ${escapeHTML(formatValue(value))}
              </td>
            `
          )
          .join('');

        return `
          <tr class="${
            different
              ? 'lpr-comparison-table__row--different'
              : ''
          }">
            <th scope="row">
              ${escapeHTML(row.label)}
            </th>

            ${cells}
          </tr>
        `;
      })
      .join('');

    content.innerHTML = `
      <div class="lpr-comparison-table-wrapper">
        <table
          class="lpr-comparison-table"
          style="--lpr-product-count: ${selectedProducts.length};"
        >
          <thead>
            <tr>
              <th
                class="lpr-comparison-table__label-heading"
                scope="col"
              >
                Caractéristiques
              </th>

              ${productHeaders}
            </tr>
          </thead>

          <tbody>
            ${tableRows}
          </tbody>
        </table>
      </div>
    `;
  }

  function openModal() {
    if (selectedProducts.length < 2) {
      return;
    }

    createInterface();
    buildComparisonTable();

    const modal = document.querySelector(
      '[data-lpr-compare-modal]'
    );

    const dialog = modal?.querySelector(
      '.lpr-compare-modal__dialog'
    );

    if (!modal || !dialog) {
      return;
    }

    lastFocusedElement = document.activeElement;
    modal.hidden = false;
    document.body.classList.add(
      'lpr-comparison-is-open'
    );

    window.requestAnimationFrame(() => {
      dialog.focus();
    });
  }

  function closeModal() {
    const modal = document.querySelector(
      '[data-lpr-compare-modal]'
    );

    if (!modal || modal.hidden) {
      return;
    }

    modal.hidden = true;
    document.body.classList.remove(
      'lpr-comparison-is-open'
    );

    if (
      lastFocusedElement &&
      typeof lastFocusedElement.focus === 'function'
    ) {
      lastFocusedElement.focus();
    }
  }

  function showTemporaryMessage(message) {
    createInterface();

    const messageElement = document.querySelector(
      '[data-lpr-temporary-message]'
    );

    if (!messageElement) {
      return;
    }

    messageElement.textContent = message;
    messageElement.hidden = false;

    window.clearTimeout(
      messageElement._lprMessageTimer
    );

    messageElement._lprMessageTimer =
      window.setTimeout(() => {
        messageElement.hidden = true;
      }, 2600);
  }

  function handleDocumentClick(event) {
  const compareButton = event.target.closest(
    '[data-lpr-compare-button]'
  );

  if (compareButton) {
    event.preventDefault();
    event.stopPropagation();
    toggleProduct(compareButton);
    return;
  }

  const removeButton = event.target.closest(
    '[data-lpr-remove-product]'
  );

  if (removeButton) {
    removeProduct(
      removeButton.dataset.lprRemoveProduct
    );
    return;
  }

  const openButton = event.target.closest(
    '[data-lpr-open-comparison]'
  );

  if (openButton && !openButton.disabled) {
    openModal();
    return;
  }

  const closeTarget = event.target.closest(
    '[data-lpr-close-comparison]'
  );

  if (closeTarget) {
    closeModal();
    return;
  }

  const modal = event.target.closest(
    '[data-lpr-compare-modal]'
  );

  const dialog = event.target.closest(
    '.lpr-compare-modal__dialog'
  );

  if (modal && !dialog) {
    closeModal();
  }
}

  function handleKeydown(event) {
    if (event.key === 'Escape') {
      closeModal();
    }
  }

  function watchProductGrid() {
    const observer = new MutationObserver(() => {
      window.clearTimeout(mutationTimer);

      mutationTimer = window.setTimeout(() => {
        updateButtons();
      }, 50);
    });

    observer.observe(document.body, {
      childList: true,
      subtree: true,
    });
  }

  function initialize() {
    loadSelection();
    createInterface();
    renderInterface();
    watchProductGrid();

    document.addEventListener(
      'click',
      handleDocumentClick
    );

    document.addEventListener(
      'keydown',
      handleKeydown
    );

    document.addEventListener(
      'shopify:section:load',
      () => {
        renderInterface();
      }
    );
  }

  if (document.readyState === 'loading') {
    document.addEventListener(
      'DOMContentLoaded',
      initialize,
      { once: true }
    );
  } else {
    initialize();
  }
})();